import { Box, Stack, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { Fragment, useMemo, useState } from "react";
import i18n from "../../../../../../i18n";
import { ChevronDownIcon, ChevronUpIcon, LightbulbIcon } from "./assets";
import type { EssayCollaborationMatch } from "./collaborationEvents";
import essayStyles from "./EssayFlaggedQuestion.module.css";
import styles from "./FlaggedQuestion.module.css";
import { lcsDiff } from "./lcs-diff";
import { findShingleOverlapRuns, tokenize, type CharRange, type Token } from "./shingle-overlap";

function getQuestionText(questionSnapshot: unknown): string | null {
	if (typeof questionSnapshot !== "object" || questionSnapshot === null) return null;
	const { questionText } = questionSnapshot as { questionText?: unknown };
	return typeof questionText === "string" ? questionText : null;
}

function getQuestionOrder(questionSnapshot: unknown): number | null {
	if (typeof questionSnapshot !== "object" || questionSnapshot === null) return null;
	const { questionOrder } = questionSnapshot as { questionOrder?: unknown };
	return typeof questionOrder === "number" ? questionOrder : null;
}

const MIN_HIGHLIGHT_RUN = 5;
const SHINGLE_SIZE = 3;

const KEY = "result.components.studentSelection.videoPlayer.flaggedQuestionTSX";

function wordRangeToCharRange(tokens: Token[], [rawStart, rawEnd]: [number, number]): CharRange | null {
	const start = Math.max(0, rawStart);
	const end = Math.min(tokens.length, rawEnd);
	if (start >= end) return null;
	return { start: tokens[start].start, end: tokens[end - 1].end };
}

const SENTENCE_END_REGEX = /[.?!]+(?=\s|$)/g;

// Widens a char range to the nearest sentence boundaries in `text` so a highlighted
// chunk never starts or ends mid-sentence.
function extendRangeToSentenceBoundaries(text: string, range: CharRange): CharRange {
	let start = 0;
	let end = text.length;

	for (const match of text.matchAll(SENTENCE_END_REGEX)) {
		const boundary = match.index + match[0].length;
		if (boundary <= range.start) start = boundary;
		if (boundary >= range.end && end === text.length) end = boundary;
	}

	while (start < text.length && /\s/.test(text[start])) start++;

	return { start, end };
}

type SentenceRange = { text: string; start: number; end: number };

// Splits `text` into sentences paired with their exact character offsets, so indices
// computed here line up with the same split done elsewhere on the same text.
function splitSentencesWithOffsets(text: string): SentenceRange[] {
	const sentences = text.split(/(?<=[.?!])\s+/);
	let cursor = 0;
	return sentences.map((sentence) => {
		const start = text.indexOf(sentence, cursor);
		const end = start + sentence.length;
		cursor = end;
		return { text: sentence, start, end };
	});
}

type SentenceMatch = { subjectIdx: number; partnerIdx: number };

type MatchGroup = { subjectRange: [number, number]; partnerRange: [number, number] };

// Merges matched sentence pairs that form a consecutive run (subject sentence N matches
// partner sentence M, N+1 matches M+1, ...) into a single range so e.g. subject 1-2
// matching partner 3-4 is treated as one collaborated passage rather than two.
function groupConsecutiveMatches(matches: SentenceMatch[]): MatchGroup[] {
	const uniqueMatches = Array.from(new Map(matches.map((m) => [`${m.subjectIdx}:${m.partnerIdx}`, m])).values()).sort(
		(a, b) => a.subjectIdx - b.subjectIdx || a.partnerIdx - b.partnerIdx,
	);

	const groups: MatchGroup[] = [];
	for (const { subjectIdx, partnerIdx } of uniqueMatches) {
		const chain = groups.find(
			(group) => group.subjectRange[1] + 1 === subjectIdx && group.partnerRange[1] + 1 === partnerIdx,
		);
		if (chain) {
			chain.subjectRange[1] = subjectIdx;
			chain.partnerRange[1] = partnerIdx;
		} else {
			groups.push({ subjectRange: [subjectIdx, subjectIdx], partnerRange: [partnerIdx, partnerIdx] });
		}
	}
	return groups;
}

function getHighlightRanges(match: EssayCollaborationMatch) {
	const subjectAnswer = match.subjectResponse.response;
	const partnerAnswer = match.partnerResponse.response;

	const sentenceMatches: SentenceMatch[] = [];

	const subjectTokens = tokenize(subjectAnswer);
	const partnerTokens = tokenize(partnerAnswer);

	const subjectSentences = splitSentencesWithOffsets(subjectAnswer);
	const partnerSentences = splitSentencesWithOffsets(partnerAnswer);

	const overlapsRange = (sentence: SentenceRange, range: CharRange) =>
		sentence.start < range.end && sentence.end > range.start;

	for (const chunk of match.chunks) {
		const rawSubjectChunkRange = wordRangeToCharRange(subjectTokens, chunk.thisRange);
		const rawPartnerChunkRange = wordRangeToCharRange(partnerTokens, chunk.otherRange);
		if (!rawSubjectChunkRange || !rawPartnerChunkRange) continue;

		const subjectChunkRange = extendRangeToSentenceBoundaries(subjectAnswer, rawSubjectChunkRange);
		const partnerChunkRange = extendRangeToSentenceBoundaries(partnerAnswer, rawPartnerChunkRange);

		const subjectChunkSentences = subjectSentences
			.map((sentence, index) => ({ ...sentence, index }))
			.filter((sentence) => overlapsRange(sentence, subjectChunkRange));
		const partnerChunkSentences = partnerSentences
			.map((sentence, index) => ({ ...sentence, index }))
			.filter((sentence) => overlapsRange(sentence, partnerChunkRange));

		for (const { index: subjectIdx, text: subjectSentence } of subjectChunkSentences) {
			for (const { index: partnerIdx, text: partnerSentence } of partnerChunkSentences) {
				if (chunk.levenshteinMet) {
					const runs = lcsDiff(subjectSentence, partnerSentence, MIN_HIGHLIGHT_RUN - 1);
					const totals = runs.reduce(
						(acc, run) => {
							acc.subjectTotal += run.end1 - run.start1;
							acc.partnerTotal += run.end2 - run.start2;
							return acc;
						},
						{ subjectTotal: 0, partnerTotal: 0 },
					);

					if (
						totals.subjectTotal / subjectSentence.length >= 0.4 ||
						totals.partnerTotal / partnerSentence.length >= 0.4
					) {
						sentenceMatches.push({ subjectIdx, partnerIdx });
					}
				}

				if (chunk.jaccardMet) {
					const runs = findShingleOverlapRuns(subjectSentence, partnerSentence, SHINGLE_SIZE);

					const subjectTotal = runs.subjectRuns.reduce((acc, run) => {
						acc += run.end - run.start;
						return acc;
					}, 0);

					const partnerTotal = runs.partnerRuns.reduce((acc, run) => {
						acc += run.end - run.start;
						return acc;
					}, 0);

					if (subjectTotal / subjectSentence.length >= 0.4 || partnerTotal / partnerSentence.length >= 0.4) {
						sentenceMatches.push({ subjectIdx, partnerIdx });
					}
				}
			}
		}
	}

	return groupConsecutiveMatches(sentenceMatches);
}

function StudentAnswer({
	name,
	answer,
	matchGroups,
	role,
}: {
	name: string;
	answer: string;
	matchGroups: MatchGroup[];
	role: "subject" | "partner";
}) {
	const sentences = splitSentencesWithOffsets(answer).map((sentence) => sentence.text);

	const segments = sentences.reduce<{ text: string; groupIndex: number | null }[]>((acc, sentence, index) => {
		const groupIndex = matchGroups.findIndex((group) => {
			const [start, end] = role === "subject" ? group.subjectRange : group.partnerRange;
			return index >= start && index <= end;
		});
		const resolvedGroupIndex = groupIndex !== -1 ? groupIndex : null;
		const previous = acc[acc.length - 1];
		if (previous && previous.groupIndex === resolvedGroupIndex && resolvedGroupIndex !== null) {
			previous.text += ` ${sentence}`;
		} else {
			acc.push({ text: sentence, groupIndex: resolvedGroupIndex });
		}
		return acc;
	}, []);

	return (
		<Stack gap="6px">
			<Typography className={essayStyles.name}>{i18n.t(`${KEY}.studentAnswerLabel`, { name })}</Typography>
			<div className={essayStyles.answerWrapper}>
				<Typography>
					{segments.map((segment, index) => {
						const className = segment.groupIndex !== null ? essayStyles.highlight : "";
						return (
							<Fragment key={index}>
								<span className={className}>{segment.text}</span>
								{segment.groupIndex !== null && <sup>{segment.groupIndex + 1}</sup>}{" "}
							</Fragment>
						);
					})}
				</Typography>
			</div>
		</Stack>
	);
}

type EssayFlaggedQuestionProps = {
	studentName: string;
	partnerName: string;
	match: EssayCollaborationMatch;
};

export default function EssayFlaggedQuestion({ studentName, partnerName, match }: EssayFlaggedQuestionProps) {
	const [open, setOpen] = useState(false);
	const [showAnswers, setShowAnswers] = useState(false);

	// getHighlightRanges runs the quadratic lcsDiff/shingle comparison across every sentence pair, so it's
	// only worth paying for once the answers are actually shown, not on every collapsed row. Kept before
	// the chunks.length early return below so hook order stays stable across renders.
	const matchGroups = useMemo(() => getHighlightRanges(match), [match]);

	if (match.chunks.length === 0) return null;

	const questionText = getQuestionText(match.questionSnapshot);

	const bannerText = i18n.t(`${KEY}.essaySimilarityBanner`, {
		count: matchGroups.length,
	}) as string;

	return (
		<Box className={styles.box}>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Typography className={styles.questionText}>
					Q{getQuestionOrder(match.questionSnapshot)}
					{" · "}
					{i18n.t(`${KEY}.questionTypeEssay`)}
					{questionText && (
						<>
							{" · "}
							<Typography
								component="span"
								className={styles.questionText}
								margin={0}
								dangerouslySetInnerHTML={{
									__html: DOMPurify.sanitize(questionText.replace("<p>", "<span>").replace("</p>", "</span>")),
								}}
							/>
						</>
					)}
				</Typography>
				<button
					type="button"
					aria-label={open ? "close" : "open"}
					onClick={() => setOpen(!open)}
					className={styles.buttonOverride}
					style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24 }}
				>
					{open ? <ChevronUpIcon /> : <ChevronDownIcon />}
				</button>
			</Stack>
			{!open && <Typography className={styles.closedBannerText}>{bannerText}</Typography>}
			{open && (
				<Stack gap="8px">
					<Box className={styles.openBanner}>
						<LightbulbIcon height="14px" />
						<Typography>{bannerText}</Typography>
					</Box>
					{showAnswers && (
						<Stack gap="12px">
							<StudentAnswer
								name={studentName}
								answer={match.subjectResponse.response}
								matchGroups={matchGroups}
								role="subject"
							/>
							<StudentAnswer
								name={partnerName}
								answer={match.partnerResponse.response}
								matchGroups={matchGroups}
								role="partner"
							/>
						</Stack>
					)}
					<button className={essayStyles.showAnswers} onClick={() => setShowAnswers(!showAnswers)}>
						{showAnswers ? i18n.t(`${KEY}.hideFullAnswers`) : i18n.t(`${KEY}.showFullAnswers`)}
					</button>
				</Stack>
			)}
		</Box>
	);
}
